import { forwardRef, type ReactNode, useImperativeHandle, useRef } from 'react';
import styled, { css } from 'styled-components';
import type { BoxProps } from '@frameio/vapor';
import {
  Box,
  mergeProps,
  ProjectAvatar,
  theme,
  useEmulateButton,
} from '@frameio/vapor';

export type ItemType = 'asset' | 'folder' | 'project';

type ItemDetailAvatarProps = {
  'aria-label'?: string;
  children: ReactNode;
  itemType: ItemType;
  onClick: () => void;
} & Omit<BoxProps, 'ref'>;

export const ProjectAvatarStyled = styled(ProjectAvatar)<{
  $isArchived?: boolean;
}>`
  cursor: pointer;
  opacity: ${(props) => (props.$isArchived ? 0.4 : 1)};

  /**
  * Targets the ProjectAvatar thumbnail or gradient inside its clipped
  * content wrapper.
  */
  & > div:first-child > :first-child {
    transform-origin: 50% 50%;
    transition:
      transform ${theme.transitions.durations[150]}
        ${theme.transitions.eases.primary},
      opacity ${theme.transitions.durations[150]}
        ${theme.transitions.eases.primary};
  }
`;

const getHoverStyles = (itemType: ItemType) => {
  switch (itemType) {
    case 'project':
      return css`
        &:hover ${ProjectAvatarStyled} {
          /**
           * Targets the ProjectAvatar thumbnail or gradient inside its clipped
           * content wrapper.
          */
          & > div:first-child > :first-child {
            transform: scale(1.05);
          }
        }

        &:active ${ProjectAvatarStyled} {
          & > div:first-child > :first-child {
            transform: scale(1);
          }
        }
      `;
    case 'folder':
      return css`
        &:hover .folder-svg {
          stroke: ${theme.colors.border.tertiary};
          stroke-width: 3px;
        }
      `;
    case 'asset':
      return '';
    default:
      return '';
  }
};

const getFocusStyles = (itemType: ItemType) => {
  switch (itemType) {
    case 'project':
      return css`
        outline: 1.5px solid ${theme.colors.border.interactive.focused};
        outline-offset: 1.5px;
      `;
    case 'folder':
      return css`
        .folder-svg {
          stroke: ${theme.colors.border.interactive.focused};
          stroke-width: 3px;
        }
      `;
    case 'asset':
      return '';
    default:
      return '';
  }
};

const StyledItemDetailAvatar = styled(Box)<{ $itemType: ItemType }>`
  border-radius: ${theme.radii[8]};
  cursor: pointer;
  outline: none;

  ${(props) => getHoverStyles(props.$itemType)}

  /* Focus state for keyboard navigation */
  &:focus-visible {
    ${(props) => getFocusStyles(props.$itemType)}
  }
`;

export const ItemDetailAvatar = forwardRef<
  HTMLDivElement,
  ItemDetailAvatarProps
>(({ itemType, children, ...props }, ref) => {
  const localRef = useRef<HTMLDivElement>(null);
  const { bind } = useEmulateButton(localRef);

  useImperativeHandle(ref, () => localRef.current!);

  return (
    <StyledItemDetailAvatar
      ref={localRef}
      $itemType={itemType}
      {...mergeProps(props, bind)}
    >
      {children}
    </StyledItemDetailAvatar>
  );
});

ItemDetailAvatar.displayName = 'ItemDetailAvatar';
